/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2024 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import { addToEndOfChain, makeHandlerChainAwaitable, replaceInChain } from "@insite/client-framework/HandlerCreator";
import { createShipTo, updateShipTo } from "@insite/client-framework/Services/CustomersService";
import { updateSession } from "@insite/client-framework/Services/SessionService";
import loadCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCart";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import loadShipTo from "@insite/client-framework/Store/Data/ShipTos/Handlers/LoadShipTo";
import SetUseOneTimeAddress from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/SetUseOneTimeAddress";
import {
    AddOrUpdateShipTo,
    chain,
    ExecuteOnSuccessCallback,
    LoadCart,
    UpdateSession,
} from "@insite/client-framework/Store/Pages/OnePageCheckout/Handlers/UpdateShipTo";

export const XngAddOrUpdateShipTo: typeof AddOrUpdateShipTo = async props => {
    if (props.parameter.shipTo.isNew) {
        props.apiResult = await createShipTo({
            shipTo: {
                ...props.parameter.shipTo,
                // XNG-Change
                properties: { ...(props.parameter.shipTo.properties || {}), webOnly: "true" },
            },
        });
    } else if (props.parameter.isEdit) {
        props.apiResult = await updateShipTo({
            billToId: props.parameter.billToId,
            shipTo: props.parameter.shipTo,
        });
    } else {
        props.apiResult = props.parameter.shipTo;
    }

    // reload to make sure we get validation
    await makeHandlerChainAwaitable(loadShipTo)({
        billToId: props.parameter.billToId,
        shipToId: props.apiResult.id,
    })(props.dispatch, props.getState);
};

const XngLoadCart: typeof LoadCart = async props => {
    const state = props.getState();
    const { cartId } = state.pages.onePageCheckout;
    await makeHandlerChainAwaitable(cartId ? loadCart : loadCurrentCart)(
        cartId ? ({ cartId, getPromotions: true } as any) : { getCartLines: true, getPromotions: true },
    )(props.dispatch, props.getState);
};

const XngDispatchCompleteUpdateShipTo: typeof ExecuteOnSuccessCallback = props => {
    const useOneTimeAddress = props.getState().pages.checkoutShipping.useOneTimeAddress;
    if (useOneTimeAddress) {
        props.dispatch(SetUseOneTimeAddress({ useOneTimeAddress: false }));
    }
};

const xngUpdateSession: typeof UpdateSession = async props => {
    const state = props.getState();
    const { cartId } = state.pages.onePageCheckout;
    if (
        !cartId &&
        props.apiResult &&
        props.parameter.shipTo?.properties?.webOnly !== "true" &&
        !props.parameter.shipTo.isNew
    ) {
        await updateSession({
            session: {
                billToId: props.parameter.billToId,
                shipToId: props.apiResult.id,
                customerWasUpdated: true,
            },
        });
    }
};

replaceInChain(chain, AddOrUpdateShipTo, XngAddOrUpdateShipTo);
replaceInChain(chain, LoadCart, XngLoadCart);
replaceInChain(chain, UpdateSession, xngUpdateSession);
addToEndOfChain(chain, XngDispatchCompleteUpdateShipTo);
